/* eslint-disable camelcase */
import { Instance, SnapshotOut, types } from "mobx-state-tree"
import { Cycle, CycleActual, CyclePrediction } from "../../../../database";
import moment from "moment";
import _ from "lodash";
import { Platform } from "react-native";
import { Toast } from "native-base";
import { withRootStore } from "../../../extensions/with-root-store"

const BatchStoreProps = {
  batchId: types.maybeNull(types.string),
  batch: types.maybeNull(types.frozen()),
  cycles: types.optional(types.array(types.frozen()), []),

  queryingCycle: types.maybeNull(types.boolean),
  lastUpdateCycle: types.maybeNull(types.string),

  queryingCycleActual: types.maybeNull(types.boolean),
  syncingCycleActual: types.maybeNull(types.boolean),
  syncedCycleActual: types.maybeNull(types.boolean),
  queryingCyclePrediction: types.maybeNull(types.boolean),
  syncingCyclePrediction: types.maybeNull(types.boolean),
  syncedCyclePrediction: types.maybeNull(types.boolean),
}

/**
 * Model description here for TypeScript hints.
 */
export const BatchStoreModel = types
  .model("BatchStore")
  .props(BatchStoreProps)
  .extend(withRootStore)
  .views((self) => ({
    get isActive() {
      return !!self.cycles.find((cycle) => !cycle.finished_at)
    },
    get todayCyclePredictions() {
      return self.cycles.filter((c) => c.age >= 0).map((cycle) => (
        (cycle.cycle_predictions || []).find((cyclePrediction) => cyclePrediction.age === cycle.age)
      ))
    },
    get todayCycleActuals() {
      return self.cycles.filter((c) => c.age >= 0).map((cycle) => (
        (cycle.cycle_actuals || []).find((cycleActual) => cycleActual.age === cycle.age)
      ))
    },
    get predictionTotalWeight() {
      return _.sumBy(this.todayCyclePredictions, 'total_weight')
    },
    get predictionSurvivalRate() {
      return _.meanBy(this.todayCyclePredictions, 'survival_rate')
    },
    get predictionSellingPrice() {
      return _.sumBy(this.todayCyclePredictions, 'selling_price')
    },
    get predictionMinWeight() {
      return _.minBy(this.todayCyclePredictions, 'weight')?.weight
    },
    get predictionMaxWeight() {
      return _.maxBy(this.todayCyclePredictions, 'weight')?.weight
    },
    get actualHarvestAccumulationWeight() {
      return _.sumBy(this.todayCycleActuals, 'harvest_accumulation_weight')
    },
    get actualFeedAccumulation() {
      return _.sumBy(this.todayCycleActuals, 'feed_accumulation')
    },
    get loadingCycle() {
      return self.queryingCycle
    },
    get loadingCycleActual() {
      return self.queryingCycleActual || self.syncingCycleActual
    },
    get loadingCyclePrediction() {
      return self.queryingCyclePrediction || self.syncingCyclePrediction
    },
    get queryingCycleActualAndPrediction() {
      return self.queryingCycleActual || self.queryingCyclePrediction
    },
    get syncingCycleActualAndPrediction() {
      return self.syncingCycleActual || self.syncingCyclePrediction
    },
    get loadingCycleActualAndPrediction() {
      return this.queryingCycleActualAndPrediction || this.syncingCycleActualAndPrediction
    },
    get loading() {
      return this.loadingCycle || this.loadingCycleActualAndPrediction;
    },
    get isFeedActualWithin4Days() {
      const cycles = self.cycles.filter((c) => !c.finished_at && c.age >= 4 && c.age < (c.target_cultivation_day || 120))
      const date = moment().subtract(4, 'days').format('YYYY-MM-DD');
      return !cycles.length || cycles.reduce((previousValue, currentValue) => previousValue && !!currentValue.cycle_actuals.find((c) => (
        c.date >= date && c.feed
      )), true)
    },
    get isFeedActualWithinWeek() {
      const cycles = self.cycles.filter((c) => !c.finished_at && c.age >= 7 && c.age < (c.target_cultivation_day || 120))
      const lastWeekDate = moment().subtract(7, 'days').format('YYYY-MM-DD');
      return !cycles.length || cycles.reduce((previousValue, currentValue) => previousValue && !!currentValue.cycle_actuals.find((c) => (
        c.date >= lastWeekDate && c.feed
      )), true)
    }
  })) // eslint-disable-line @typescript-eslint/no-unused-vars
  .actions((self) => ({
    set(key: keyof typeof BatchStoreProps, value: any) {
      if (self[key] === value) return;
      self[key] = value as never;
    },
    async getCycles() {
      const { appStore: { farmStore: { proPonds } } } = self.rootStore
      this.set('queryingCycle', true);
      const cycles = await Cycle.fetch({
        farm_id__in: self.batch.farm_id,
        started_at__gte: self.batch.start_batch,
        started_at__lte: self.batch.end_batch,
        sort: 'pond_name_alpha_numeric|started_at,desc|id,desc'
      }).catch((e) => {
        console.warn(e);
        return self.cycles || []
      })
      this.set('cycles', cycles.map((cycle: Cycle) => {
        const c = self.cycles.find((c) => c.id === cycle.getKey()) || {}
        return {
          ...cycle.toArray(),
          pro: proPonds.find((p) => p.id === cycle.getAttribute('pond_id')) || cycle.getAttribute('pro'),
          cycle_predictions: c.cycle_predictions || [],
          cycle_actuals: c.cycle_actuals || [],
        }
      }))
      this.set('lastUpdateCycle',  moment.utc().format())
      this.set('queryingCycle', false);
    },
    async getCycleActuals(sync = true, onFinishSync = undefined) {
      if (!sync) {
        this.set('queryingCycleActual', true);
        const cycles = self.cycles;
        const cycleIds = cycles.map((cycle) => cycle.id)
        const cycleActuals = await CycleActual.fetch({
          cycle_id__in: cycleIds.join(','),
          sort: 'age,desc',
          generate_by_cycle_id: cycleIds.join(','),
        }).catch((e) => {
          console.warn(e)
          return []
        });
        this.set('cycles', cycles.map((cycle: any) => ({
          ...cycle,
          cycle_actuals: cycleActuals.filter((c: CycleActual) => c.getAttribute('cycle_id') === cycle.id).map((c: CycleActual) => c.toArray()),
        })))
        this.set('lastUpdateCycle',  moment.utc().format())
        this.set('queryingCycleActual', false);
      }
      if (sync) {
        this.set('queryingCycleActual', true);
        this.syncCycleActuals().then(async () => {
          await this.getCycleActuals(false).catch()
          onFinishSync && onFinishSync()
          this.set('syncedCycleActual', true);
          this.set('queryingCycleActual', false);
        })
      }
    },
    async syncCycleActuals() {
      if (Platform.OS === 'web') return true;

      const cycles = self.cycles
      const cycleIds = cycles.map((cycle) => cycle.id);
      return CycleActual.syncAll({
        cycle_id__in: cycleIds.join(','),
        generate_by_cycle_id: cycleIds.join(',')
      }, [
        ['cycle_actuals.cycle_id', cycleIds],
      ], true, (p) => {
        this.set('syncingCycleActual', p !== null && p !== 100)
      }, (e) => {
        if (e.message && !Toast.isActive(e.name)) Toast.show({ id: e.name, title: e.message });
      })
    },
    async getCycleActualAndPredictions(sync = true, onFinishSync = undefined) {
      if (!sync) {
        this.set('queryingCycleActual', true);
        this.set('queryingCyclePrediction', true);
        const cycles = self.cycles;
        const cycleIds = cycles.map((cycle) => cycle.id)
        const cycleAges = _.uniq(cycles.flatMap((cycle) => [...Array(5)].map((v, index) => (cycle.age + 1 - index))));
        const [ cyclePredictions, cycleActuals ] = await Promise.all([
          CyclePrediction.fetch({
            cycle_id__in: cycleIds.join(','),
            age__in: cycleAges.join(','),
            sort: 'age,desc',
            generate_by_cycle_id: cycleIds.join(','),
          }).catch((e) => {
            console.warn(e)
            return []
          }),
          CycleActual.fetch({
            cycle_id__in: cycleIds.join(','),
            age__in: cycleAges.join(','),
            sort: 'age,desc',
            generate_by_cycle_id: cycleIds.join(','),
          }).catch((e) => {
            console.warn(e)
            return []
          }),
        ])
        this.set('cycles', cycles.map((cycle: any) => {
          const cycle_actuals = _.orderBy(_.uniqBy([
            ...cycleActuals.filter((c: CycleActual) => c.getAttribute('cycle_id') === cycle.id).map((c: CycleActual) => c.toArray()),
            ...cycle.cycle_actuals,
          ], 'age'), ['age'], ['desc'])

          return ({
            ...cycle,
            cycle_predictions: cyclePredictions.filter((c: CyclePrediction) => c.getAttribute('cycle_id') === cycle.id).map((c: CyclePrediction) => c.toArray()),
            cycle_actuals
          })
        }))
        this.set('lastUpdateCycle',  moment.utc().format())
        this.set('queryingCycleActual', false);
        this.set('queryingCyclePrediction', false);
      }
      if (sync) {
        this.set('queryingCycleActual', true);
        this.set('queryingCyclePrediction', true);
        this.syncCycleActualAndPredictions().then(async () => {
          await this.getCycleActualAndPredictions(false).catch()
          onFinishSync && onFinishSync()
          this.set('syncedCycleActual', true)
          this.set('syncedCyclePrediction', true)
          this.set('queryingCycleActual', false);
          this.set('queryingCyclePrediction', false);
        })
      }
    },
    async syncCycleActualAndPredictions() {
      if (Platform.OS === 'web') return true;

      const cycles = self.cycles
      const cycleIds = cycles.map((cycle) => cycle.id);
      const cycleAges = _.uniq(cycles.flatMap((cycle) => [...Array(5)].map((v, index) => (cycle.age + 1 - index))));
      return (await Promise.all([
        CycleActual.syncAll({
          cycle_id__in: cycleIds.join(','),
          age__in: cycleAges.join(','),
          generate_by_cycle_id: cycleIds.join(',')
        }, [
          ['cycle_actuals.cycle_id', cycleIds],
          ['cycle_actuals.age', cycleAges]
        ], false, (p) => {
          this.set('syncingCycleActual', p !== null && p !== 100)
        }, (e) => {
          if (e.message && !Toast.isActive(e.name)) Toast.show({ id: e.name, title: e.message });
        }),
        CyclePrediction.syncAll({
          cycle_id__in: cycleIds.join(','),
          age__in: cycleAges.join(','),
          generate_by_cycle_id: cycleIds.join(',')
        }, [
          ['cycle_predictions.cycle_id', cycleIds],
          ['cycle_predictions.age', cycleAges]
        ], false, (p) => {
          this.set('syncingCyclePrediction', p !== null && p !== 100)
        }, (e) => {
          if (e.message && !Toast.isActive(e.name)) Toast.show({ id: e.name, title: e.message });
        })
      ])).reduce((previousValue, currentValue) => previousValue && currentValue, true)
    },
    init() {
      this.getCycles()
        .then(() => this.getCycleActualAndPredictions())
        .catch()
    },
    initWithCycleActuals() {
      this.getCycles()
        .then(() => this.getCycleActualAndPredictions())
        .then(() => this.getCycleActuals())
        .catch()
    }
  })) // eslint-disable-line @typescript-eslint/no-unused-vars

type BatchStoreType = Instance<typeof BatchStoreModel>
export interface BatchStore extends BatchStoreType {}
type BatchStoreSnapshotType = SnapshotOut<typeof BatchStoreModel>
export interface BatchStoreSnapshot extends BatchStoreSnapshotType {}
export const createBatchStoreDefaultModel = () => types.optional(BatchStoreModel, {})
